'use client'

import './Calculator.css'

import type { RateTable } from '@/utils/financing'
import type { ChangeEvent } from 'react'
import type { PersonalFinancingRates } from './types'
import { calculator, CustomSlider, marks } from '@/constants/calculator'

import { useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { isEmpty } from 'lodash'

import useStore from '@/stores/Store'
import api from '@/utils/api'
import {
  calculateMonthlyInstallment,
  formatInstallment,
  parseRateTable,
  validateFinancingAmount
} from '@/utils/financing'

const MIN_TENURE = 2
const MAX_TENURE = 10
const AMOUNT_BOUNDS = { min: 10000, max: 250000 }
const AMOUNT_RANGE_ERROR = 'Please enter an amount between RM10,000 and RM250,000.'

const CalculatorEducationPFi = () => {
  const [financingAmount, setFinancingAmount] = useState('')
  const [tenure, setTenure] = useState(MIN_TENURE)
  const [monthlyPayment, setMonthlyPayment] = useState('0.00')
  const [isActive, setIsActive] = useState(false)
  const [amountError, setAmountError] = useState('')
  const [isCalculated, setIsCalculated] = useState(false)

  const [incomeHigh, setIncomeHigh] = useState<RateTable>({})
  const { calculatorState, setCalculatorState } = useStore()

  const handleFinancingAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replaceAll(/[^0-9]/g, '')
    setFinancingAmount(value)
    setAmountError('')
  }

  const handleAmountBlur = () => {
    setIsActive(false)
    if (!financingAmount) return
    if (validateFinancingAmount(financingAmount, AMOUNT_BOUNDS).status === 'out-of-range') {
      setAmountError(AMOUNT_RANGE_ERROR)
    }
  }

  const calculateTotal = () => {
    // Status only — deliberately NOT `result.amount`. Unlike the other five
    // calculators this one never snaps the amount to the nearest RM100, and
    // validateFinancingAmount's `valid` branch returns a rounded amount.
    const result = validateFinancingAmount(financingAmount, AMOUNT_BOUNDS)
    if (result.status === 'required') {
      setAmountError(calculator.amountErrorRequired)
      setMonthlyPayment('0.00')
      return
    }
    if (result.status === 'out-of-range') {
      setAmountError(AMOUNT_RANGE_ERROR)
      setMonthlyPayment('0.00')
      return
    }
    const amount = Number.parseFloat(financingAmount)

    setIsCalculated(true)
    setAmountError('')

    const rate = incomeHigh[tenure]
    if (!rate || tenure < MIN_TENURE || tenure > MAX_TENURE) {
      setMonthlyPayment('0.00')
      return
    }

    const total = calculateMonthlyInstallment(amount, rate, tenure)

    setMonthlyPayment(formatInstallment(total))
  }

  useEffect(() => {
    if (isCalculated && tenure >= MIN_TENURE && tenure <= MAX_TENURE) {
      calculateTotal()
    }
  }, [tenure])

  useEffect(() => {
    if (isCalculated) {
      calculateTotal()
    }
  }, [incomeHigh])

  useEffect(() => {
    if (!isCalculated) return
    const amount = Number.parseInt(financingAmount, 10)
    if (!financingAmount || Number.isNaN(amount) || amount < 10000 || amount > 250000) {
      setMonthlyPayment('0.00')
      return
    }
    calculateTotal()
  }, [financingAmount])

  const handleAssign = (data: PersonalFinancingRates) => {
    const incomeLowData = data.PersonalFinancingI?.[2]?.incomeLowRange
    const incomeHighData = data.PersonalFinancingI?.[2]?.incomeHighRange
    // Transform the array into an object with { item.tenure: item.rate }
    const incomeHighObject = parseRateTable(incomeHighData)

    // Set the state with the transformed object
    setIncomeHigh(incomeHighObject)
    // Gate on the RAW CMS data, not the parsed tables: parseRateTable returns
    // `{}` (truthy) for missing data where the old `?.reduce` returned
    // `undefined` (falsy), so testing the parsed values would flip this guard
    // open and reset the tenure before any rates have loaded.
    if (incomeLowData && incomeHighData) {
      const availableTenures = Object.keys(incomeHighObject)
        .map(Number)
        .filter(year => year >= MIN_TENURE && year <= MAX_TENURE)
        .sort((a, b) => a - b)

      setTenure(availableTenures[0] ?? MIN_TENURE)
    }
  }

  const fetchPFIData = async () => {
    try {
      const response = await api.get('/personal-financings?pLevel')
      const data = response.data.data[0]
      if (!data) return

      setCalculatorState(data)
      handleAssign(data)
    } catch (error) {
      console.error('Error fetching data', error)
    }
  }

  // DELIBERATE CLIENT REFETCH (derived 2026-08-17 from Task 5) — do not remove.
  // Profit rates drive a figure the customer acts on; publish-to-deploy latency
  // (3-15 min) is too long to compute against a superseded rate. The host routes
  // are excluded from tests/no-client-cms-fetch.spec.ts. See Task 9 of
  // docs/superpowers/plans/2026-08-17-remove-client-side-cms-refetch.md
  useEffect(() => {
    if (isEmpty(calculatorState)) {
      fetchPFIData()
    } else {
      handleAssign(calculatorState)
    }
  }, [])

  return (
    <div id="calculator" className="calculator section-calc light-background calculator-form">
      <div data-aos="fade-up" data-aos-delay="200" className="w-100">
        <p className="header">{calculator.header}</p>

        <div className={`form-group-calc ${isActive ? 'active' : ''}`}>
          <label htmlFor="financing-amount" className="income-group income-group-text ">
            {calculator.financingAmountEducation}
          </label>
          <div className="input-group">
            <span className="amount">RM</span>
            <input
              type="text"
              id="financing-amount"
              name="financing-amount"
              placeholder={calculator.insertAmountPlaceholder}
              className={`form-control-input ${amountError ? 'input-error' : ''}`}
              value={financingAmount}
              onChange={handleFinancingAmountChange}
              onFocus={() => setIsActive(true)}
              onBlur={handleAmountBlur}
              inputMode="numeric"
              maxLength={6}
              style={{ border: 'none' }}
              aria-invalid={!!amountError}
              aria-describedby={amountError ? 'financing-amount-error' : undefined}
            />
          </div>
          {amountError && (
            <div id="financing-amount-error" className="error-message" role="alert">
              {amountError}
            </div>
          )}
        </div>

        <div className="form-group-2">
          <label htmlFor="tenure" className="tenure">
            {calculator.tenureLabel}
          </label>
          <div className="input-group">
            <CustomSlider
              value={tenure}
              onChange={(_: unknown, value: number) => {
                if (Array.isArray(value)) return
                setTenure(value)
                setIsCalculated(true)
              }}
              step={1}
              marks={marks}
              min={MIN_TENURE}
              max={MAX_TENURE}
              valueLabelDisplay="off"
              aria-label="Financing Tenure"
            />
          </div>
          <p className="payment">{calculator.monthlyPaymentLabel}</p>

          <Container>
            <Row className="align-items-center justify-content-between row-cols-2">
              <Col xs={8} className="ps-0">
                <div className="form-group-3">
                  <div className="input-group center-input-group">
                    <span className="input-group-text-2">RM</span>
                    <span className="financing-amount-style" aria-live="polite">
                      {monthlyPayment}
                    </span>
                  </div>
                </div>
              </Col>
              <Col xs={4} className="pe-0">
                <button
                  type="button"
                  className="btn btn-primary calculate-button w-100"
                  onClick={calculateTotal}
                >
                  {calculator.calculateTotalButton}
                </button>
              </Col>
            </Row>
          </Container>
        </div>
        <span className="notes">
          The final financing amount, monthly financing payment, and the profit rates are subject to
          the bank's assessment and approval.
        </span>
      </div>
    </div>
  )
}

export default CalculatorEducationPFi
